import type {
  MergeRowDto,
  PeriodBoundsDto,
  TabPeriodSpanDto,
} from '../api/mergeResolution.types';

import type { PeriodRangeMode } from './types';

const NUMERIC_KEY = /^\d{1,15}$/;

// 1-15 digits are exact in a JS Number; longer keys count as non-numeric
// and are always shown.
export function isNumericKey(key: string | null): boolean {
  return key !== null && NUMERIC_KEY.test(key);
}

function isValidBounds(raw: unknown): raw is PeriodBoundsDto {
  if (typeof raw !== 'object' || raw === null) return false;
  const { From, To } = raw as Record<string, unknown>;
  return (
    typeof From === 'string' &&
    typeof To === 'string' &&
    NUMERIC_KEY.test(From) &&
    NUMERIC_KEY.test(To) &&
    Number(From) <= Number(To)
  );
}

// Fail safe = show: a malformed entry is dropped, and dropping only shows
// more columns.
export function validSpans(raw: unknown): Map<string, TabPeriodSpanDto> {
  const spans = new Map<string, TabPeriodSpanDto>();
  if (!Array.isArray(raw)) return spans;
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { Tab_Name, Changed, NeedsDecision, ChangesOutsideNeedsDecision } =
      entry as Record<string, unknown>;
    if (Tab_Name !== null && typeof Tab_Name !== 'string') continue;
    if (!isValidBounds(Changed)) continue;
    if (
      NeedsDecision !== null &&
      NeedsDecision !== undefined &&
      !isValidBounds(NeedsDecision)
    ) {
      continue;
    }
    const outside =
      typeof ChangesOutsideNeedsDecision === 'number' &&
      Number.isInteger(ChangesOutsideNeedsDecision) &&
      ChangesOutsideNeedsDecision >= 0
        ? ChangesOutsideNeedsDecision
        : null;
    spans.set(Tab_Name ?? '', {
      Tab_Name: Tab_Name as string | null,
      Changed,
      NeedsDecision:
        (NeedsDecision as PeriodBoundsDto | null | undefined) ?? null,
      ChangesOutsideNeedsDecision: outside,
    });
  }
  return spans;
}

export function effectiveSpan(
  span: TabPeriodSpanDto,
  mode: PeriodRangeMode
): PeriodBoundsDto {
  return mode === 'needs_decision' && span.NeedsDecision
    ? span.NeedsDecision
    : span.Changed;
}

export function visiblePeriodKeys(
  periodKeys: (string | null)[],
  rows: MergeRowDto[],
  spans: Map<string, TabPeriodSpanDto>,
  mode: PeriodRangeMode
): (string | null)[] {
  if (spans.size === 0) return periodKeys;
  return periodKeys.filter((key) => {
    if (key === null || !isNumericKey(key)) return true;
    const value = Number(key);
    return rows.some((row) => {
      if (!row.Periods.some((period) => period.DateID === key)) return false;
      const span = spans.get(row.Tab_Name ?? '');
      if (!span) return true;
      const { From, To } = effectiveSpan(span, mode);
      return value >= Number(From) && value <= Number(To);
    });
  });
}

// inputKey is the Tab_Name string when there is no ByInput, so it keys the
// spans map directly.
export function activeTab(
  inputKey: string | null,
  rows: MergeRowDto[],
  spans: Map<string, TabPeriodSpanDto>
): string | null {
  if (inputKey !== null && spans.has(inputKey)) return inputKey;
  const tabs = new Set(rows.map((row) => row.Tab_Name ?? ''));
  return tabs.size === 1 ? [...tabs][0] : null;
}

export function rangeLabel(
  bounds: PeriodBoundsDto,
  periodLabel: (dateId: string | null) => string
): string {
  const from = periodLabel(bounds.From);
  return bounds.From === bounds.To
    ? from
    : `${from} – ${periodLabel(bounds.To)}`;
}
